// src/services/employeeAverageReportsService.js
import { averagereportsAPI } from './api';
import { downloadBlobResponse, extensionForFormat } from '../utils/downloadFile';

export const employeeAverageReportsService = {
  // Get surveys for dropdown selection
  async getSurveys() {
    try {
      console.log('🔄 Fetching surveys for employee average reports');
      const response = await averagereportsAPI.getSurveys();
      console.log('✅ Surveys loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching surveys:', error);
      throw new Error('Failed to load surveys: ' + error.message);
    }
  },

  // src/services/employeeAverageReportsService.js - Updated getEmployees method

// Get employees by survey for selection
// src/services/employeeAverageReportsService.js

// Get employees by survey for selection
async getEmployees(surveyId) {
  try {
    console.log('🔄 Fetching employees for survey:', surveyId);

    if (!surveyId) {
      throw new Error('Survey ID is required');
    }

    // Use the correct endpoint - GET with surveyId in URL
    const response = await averagereportsAPI.getEmployees(surveyId);
    console.log('✅ Employees loaded:', response.data.length, 'employees');
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching employees:', error);

    // Provide more specific error messages
    if (error.response?.status === 404) {
      throw new Error('No employees found for this survey');
    } else if (error.response?.status === 500) {
      throw new Error('Server error while loading employees');
    } else {
      throw new Error('Failed to load employees: ' + (error.message || 'Unknown error'));
    }
  }
},

  // Generate comprehensive employee average reports
  async generateReport(request) {
    try {
      console.log('🔄 Generating employee average reports:', {
        surveyId: request.surveyId,
        employeeCount: request.employeeIds?.length || 0,
        dateRange: {
          from: request.fromDate,
          to: request.toDate
        }
      });

      const response = await averagereportsAPI.generateReport(request);

      console.log('✅ Employee average reports generated successfully:', {
        overallScore: response.data.overallScore,
        totalEmployees: response.data.totalEmployees,
        totalResponses: response.data.totalResponses,
        responseRate: response.data.responseRate,
        categories: response.data.categories?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error generating employee average reports:', error);
      throw new Error('Failed to generate reports: ' + error.message);
    }
  },

  // Generate detailed category report
  async generateCategoryReport(request) {
    try {
      console.log('🔄 Generating category report:', {
        surveyId: request.surveyId,
        dateRange: { from: request.fromDate, to: request.toDate }
      });

      const response = await averagereportsAPI.generateReport(request);
      console.log('✅ Category report generated:', response.data.categories?.length || 0, 'categories');
      return response.data;
    } catch (error) {
      console.error('❌ Error generating category report:', error);
      throw new Error('Failed to generate category report: ' + error.message);
    }
  },

  // Generate question-level detailed report
  async generateQuestionReport(request) {
    try {
      console.log('🔄 Generating question report:', {
        surveyId: request.surveyId,
        dateRange: { from: request.fromDate, to: request.toDate }
      });

      const response = await averagereportsAPI.generateReport(request);

      // Extract question-level data from the response
      const questionData = {
        totalQuestions: response.data.categories?.reduce((total, cat) => total + (cat.questions?.length || 0), 0) || 0,
        averageScores: response.data.categories?.flatMap(cat =>
          cat.questions?.map(q => ({
            questionText: q.questionText,
            averageScore: q.averageScore,
            responseCount: q.responseCount
          })) || []
        ) || []
      };

      console.log('✅ Question report generated:', questionData.totalQuestions, 'questions');
      return questionData;
    } catch (error) {
      console.error('❌ Error generating question report:', error);
      throw new Error('Failed to generate question report: ' + error.message);
    }
  },

  // Get report statistics and summary
  async getReportSummary(surveyId) {
    try {
      console.log('🔄 Fetching employee average report summary for survey:', surveyId);

      const request = {
        surveyId: surveyId,
        employeeIds: [],
        fromDate: null,
        toDate: null
      };

      const response = await averagereportsAPI.generateReport(request);

      const summary = {
        surveyId: surveyId,
        overallScore: response.data.overallScore,
        totalEmployees: response.data.totalEmployees,
        totalResponses: response.data.totalResponses,
        responseRate: response.data.responseRate,
        categoriesCount: response.data.categories?.length || 0,
        questionsCount: response.data.categories?.reduce((total, cat) => total + (cat.questions?.length || 0), 0) || 0,
        surveyPeriod: response.data.surveyPeriod
      };

      console.log('✅ Employee average report summary loaded:', summary);
      return summary;
    } catch (error) {
      console.error('❌ Error fetching employee average report summary:', error);
      throw new Error('Failed to load report summary: ' + error.message);
    }
  },

  // Export the report as a real Excel or PDF file, downloaded straight to the browser.
  // This used to be a stub that never called the backend at all.
  async exportReport(request, format = 'excel') {
    try {
      console.log('🔄 Exporting employee average report:', {
        format,
        surveyId: request.surveyId,
        employeeCount: request.employeeIds?.length || 0
      });

      const response = await averagereportsAPI.exportReport(request, format);
      downloadBlobResponse(response, `average-report-survey-${request.surveyId}.${extensionForFormat(format)}`);

      console.log('✅ Employee average report exported successfully');
      return { success: true, message: 'Report exported successfully' };
    } catch (error) {
      console.error('❌ Error exporting employee average report:', error);
      // error.response?.data carries the real backend message once the api.js
      // response interceptor has decoded the error Blob -- fall back to the
      // generic axios message only if that decoding didn't happen.
      throw new Error('Failed to export report: ' + (error.response?.data || error.message));
    }
  },

  // Quick report - generate with minimal parameters
  async generateQuickReport(surveyId) {
    try {
      console.log('🔄 Generating quick employee average report:', { surveyId });

      const request = {
        surveyId: surveyId,
        employeeIds: [],
        fromDate: null,
        toDate: null
      };

      const response = await averagereportsAPI.generateReport(request);

      console.log('✅ Quick employee average report generated:', {
        overallScore: response.data.overallScore,
        totalResponses: response.data.totalResponses,
        categories: response.data.categories?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error generating quick employee average report:', error);
      throw new Error('Failed to generate quick report: ' + error.message);
    }
  },

  // Validate report request before sending
  validateReportRequest(request) {
    const errors = [];

    if (!request.surveyId || request.surveyId <= 0) {
      errors.push('Valid Survey ID is required');
    }

    if (request.fromDate && request.toDate && new Date(request.fromDate) > new Date(request.toDate)) {
      errors.push('From date cannot be after To date');
    }

    if (request.employeeIds && !Array.isArray(request.employeeIds)) {
      errors.push('Employee IDs must be an array');
    }

    const isValid = errors.length === 0;

    console.log('🔍 Employee average report request validation:', {
      isValid,
      errors: isValid ? 'None' : errors
    });

    return {
      isValid,
      errors
    };
  }
};

export default employeeAverageReportsService;
